import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { usePrerequisites } from "../components/GettingStarted";
import { Icon } from "../components/icons";
import { PlanPicker, defaultPlan, formatPrice } from "../components/PlanPicker";
import { RegionSection } from "../components/RegionSection";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  PageHeader,
  cx,
} from "../components/ui";
import { QuotaAlert, quotaRefusal, type QuotaRefusal } from "../components/Quota";
import { ApiError } from "../lib/api";
import { ENGINES } from "../lib/engines";
import { useCreateDatabase, usePlans, useRegions } from "../lib/queries";
import type { DatabaseEngine } from "../lib/types";

/*
 * The form that used to hang off a modal on the list page, promoted to a page
 * of its own so it matches the app-creation workflow. The decisions are the
 * same three the modal always held — a database has no source to pick — but
 * each now has room for the facts that make it: the engine's port and backup
 * support, the hostname the name becomes, the cost of the plan.
 */
export function NewDatabasePage() {
  const { t } = useTranslation("create");
  const { currentOrg } = useAuth();
  const navigate = useNavigate();
  const createDatabase = useCreateDatabase(currentOrg?.id ?? "");

  const plans = usePlans(currentOrg?.id, "database");
  const regions = useRegions(currentOrg?.id);
  const prerequisites = usePrerequisites(currentOrg?.id);

  const [engine, setEngine] = useState<DatabaseEngine>("postgres");
  const [name, setName] = useState("");
  const [planId, setPlanId] = useState<string | null>(null);
  // Null is Auto, and Auto is the default: what every database got before a
  // region could be chosen, and right for anyone without a reason to override.
  const [region, setRegion] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A refused create that was the ceiling, not a mistake: rendered with the numbers and the way to ask.
  const [refusal, setRefusal] = useState<QuotaRefusal | null>(null);

  const activeEngine = ENGINES.find((option) => option.id === engine)!;
  const selectedPlan = (plans.data ?? []).find((plan) => plan.id === planId) ?? null;

  /*
   * Preselected, the same way the app form does it: an unset plan was the one
   * thing that made this form impossible to submit without a decision a
   * first-time customer has no basis for. `defaultPlan` holds the rule — the
   * smallest tier that runs an ordinary workload, not the smallest sold — and
   * the choice is only ever filled in, never overwritten.
   */
  useEffect(() => {
    if (planId !== null) return;
    const chosen = defaultPlan(plans.data ?? []);
    if (chosen) setPlanId(chosen.id);
  }, [plans.data, planId]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setRefusal(null);
    if (!planId) {
      setError(t("database.pickPlan"));
      return;
    }

    try {
      const created = await createDatabase.mutateAsync({
        name: name.trim(),
        engine,
        planId,
        region,
      });
      // Straight to the new database's own page: a database nobody can connect
      // to is not yet useful, and its overview is where the address lives.
      // Previously this handed the record over through history state so the
      // list could open a connection modal — there is a real route to send
      // them to now, which also means the URL is right if they reload.
      void navigate(`/databases/${created.id}`);
    } catch (cause) {
      setRefusal(quotaRefusal(cause));
      setError(cause instanceof ApiError ? cause.message : t("database.failed"));
    }
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title={t("database.title")}
      />

      <form onSubmit={submit} className="space-y-6">
        {refusal ? <QuotaAlert refusal={refusal} /> : error && <Alert>{error}</Alert>}

        {/*
          The API refuses to create a database on an empty balance exactly as it
          does for apps; saying so here rather than letting the submit fail.
        */}
        {prerequisites.needsCredit && (
          <Alert tone="warning">
            <span className="font-medium">{t("shared.noCredit")}</span> {t("database.creditNote")}{" "}
            <Link to="/settings/billing" className="font-medium underline">
              {t("shared.addCredit")}
            </Link>
          </Alert>
        )}

        <Card>
          <CardHeader title={t("database.engine")} />
          <div className="space-y-4 p-5">
            <div
              role="radiogroup"
              aria-label={t("database.engine")}
              className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3"
            >
              {ENGINES.map((option) => {
                const selected = option.id === engine;
                return (
                  <button
                    key={option.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setEngine(option.id)}
                    className={cx(
                      "border p-3.5 text-left transition-colors",
                      selected
                        ? "border-accent bg-accent-soft"
                        : "border-line hover:border-line-strong hover:bg-raised",
                    )}
                  >
                    <span className="flex items-start justify-between gap-2">
                      <span className="text-sm font-medium text-fg">{option.label}</span>
                      {selected && <Icon name="check" className="size-4 shrink-0 text-accent" />}
                    </span>
                    <span className="mt-0.5 block text-xs leading-5 text-fg-muted">
                      {t(`engine.${option.id}` as "engine.postgres")}
                    </span>
                    {/* Facts from the engine spec rather than decoration: the
                        port it listens on, and whether anything can back it
                        up — the one line that differs between engines. */}
                    <span className="mt-2 block font-mono text-xs text-fg-subtle tabular-nums">
                      :{option.port} ·{" "}
                      {option.backups ? t("database.backups") : t("database.noBackups")}
                    </span>
                  </button>
                );
              })}
            </div>

            {/*
              The one engine with a caveat gets it at the moment of choosing,
              not later on the backups panel. The others need no commentary.
            */}
            {!activeEngine.backups && (
              <Alert tone="warning">{t("database.noRedisBackups")}</Alert>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title={t("database.details")} />
          <div className="p-5">
            <Field
              label={t("database.name")}
              htmlFor="db-name"
              hint={t("database.nameHint")}
            >
              <Input
                id="db-name"
                required
                value={name}
                placeholder={t("database.namePlaceholder")}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
          </div>
        </Card>

        <RegionSection
          catalogue={regions.data}
          loading={regions.isLoading}
          value={region}
          onChange={setRegion}
        />

        <Card>
          <CardHeader title={t("database.plan")} />
          <div className="p-5">
            <PlanPicker
              plans={plans.data ?? []}
              loading={plans.isLoading}
              value={planId}
              onChange={setPlanId}
            />
          </div>
        </Card>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-6">
          <Button type="button" variant="ghost" onClick={() => void navigate("/databases")}>
            {t("shared.cancel")}
                        </Button>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {/* The figure that will leave their balance, beside the button that
                commits to it — the same pairing the app review step makes. */}
            {selectedPlan && (
              <p className="text-sm text-fg-subtle">
                {formatPrice(selectedPlan.price.cents, selectedPlan.price.currency)}/mo max, billed
                hourly
              </p>
            )}
            <Button
              type="submit"
              variant="inverse"
              loading={createDatabase.isPending}
              disabled={prerequisites.needsCredit}
            >
              {t("database.create")}
                            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
